"use client";

import Image from "next/image";
import React from "react";

const Placeholder = ({ className }: { className: string }) => (
  <div className={`rounded bg-white/[0.08] ${className}`} aria-hidden="true" />
);

const CHOICE_WIDTHS = ["w-16", "w-20", "w-14", "w-24"] as const;

export const ExerciseSkeleton: React.FC = () => (
  <div className="min-h-[100dvh] bg-[#0F0F14] text-white" aria-busy="true">
    <header className="sticky top-0 z-10 border-b border-white/10 bg-[#0F0F14]">
      <div className="mx-auto flex min-h-16 max-w-5xl items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-[#1B1C22]">
            <span className="h-3.5 w-3.5 rounded-sm bg-[#FD954F]/70" aria-hidden="true" />
          </div>
          <div className="space-y-2">
            <Placeholder className="h-3 w-24" />
            <Placeholder className="h-2.5 w-14 bg-white/[0.05]" />
          </div>
        </div>
        <div className="flex gap-2">
          <Placeholder className="h-10 w-20 border border-white/[0.04] bg-transparent" />
          <Placeholder className="h-10 w-16 border border-white/[0.06] bg-white/[0.06]" />
        </div>
      </div>
    </header>

    <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:py-8">
      <div className="mb-5 flex items-center justify-between border-b border-white/10 pb-5">
        <div className="space-y-2">
          <Placeholder className="h-4 w-32" />
          <Placeholder className="h-3 w-52 bg-white/[0.05]" />
        </div>
        <Placeholder className="h-10 w-28 border border-white/[0.04] bg-transparent" />
      </div>

      <section
        className="overflow-hidden rounded-lg border border-white/10 bg-[#15161B]"
        data-exercise-skeleton
      >
        <div className="p-5 sm:p-7">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full border border-white/15 bg-black/40">
                <Image
                  src="/ui/brand-kit/marks/chickytutor-face-mark-128.png"
                  alt=""
                  width={32}
                  height={32}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <Placeholder className="h-3 w-36 bg-[#FD954F]/30" />
                <Placeholder className="h-2.5 w-20 bg-white/[0.05]" />
              </div>
            </div>
            <Placeholder className="h-10 w-10 border border-white/[0.06] bg-transparent" />
          </div>

          <div className="border-b border-white/10 pb-6" data-exercise-skeleton-prompt>
            <div className="flex flex-wrap items-end gap-x-2 gap-y-3">
              <Placeholder className="h-7 w-24" />
              <Placeholder className="h-7 w-16" />
              <div
                className="h-8 w-24 rounded-sm border-b-2 border-[#FD954F] bg-[#FD954F]/10"
                data-exercise-skeleton-gap
                aria-hidden="true"
              />
              <Placeholder className="h-7 w-20" />
              <Placeholder className="h-7 w-28" />
            </div>
            <Placeholder className="mt-4 h-3 w-2/5 bg-white/[0.05]" />
            <Placeholder className="mt-2 h-4 w-3/5 bg-white/[0.06]" />
          </div>

          <div className="mt-6">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <Placeholder className="h-3.5 w-20" />
              <div className="flex gap-2">
                <Placeholder className="h-8 w-20 bg-white/[0.05] sm:w-24" />
                <Placeholder className="h-8 w-20 bg-white/[0.05] sm:w-24" />
              </div>
            </div>
            <Placeholder className="h-12 w-full border border-white/[0.05] bg-[#1B1C22]" />
          </div>

          <div className="mt-5 rounded-xl border border-white/[0.06] bg-[#121318] p-3 sm:p-4">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
              {CHOICE_WIDTHS.map((width, index) => (
                <div
                  key={width}
                  className="relative flex min-h-16 items-center justify-center rounded-xl border border-white/[0.08] bg-[#1A1B22] px-3 py-2.5"
                  data-exercise-skeleton-choice
                  aria-hidden="true"
                >
                  <span className="absolute left-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-md border border-white/[0.08] bg-white/[0.04] font-mono text-[10px] text-white/30">
                    {index + 1}
                  </span>
                  <Placeholder className={`h-4 max-w-[65%] ${width} bg-white/[0.07]`} />
                </div>
              ))}
            </div>
          </div>

          <Placeholder className="mt-6 h-14 w-full bg-[#FD954F]/25" />
        </div>

        <div className="border-t border-white/[0.08] bg-[#121318] px-5 py-4 sm:px-7">
          <div className="border-l-2 border-[#FD954F]/65 pl-3" role="status" aria-live="polite">
            <p className="text-sm font-medium text-white/80">Preparing your first exercise</p>
            <p className="mt-0.5 text-xs text-white/45">
              Building the sentence and answer choices.
            </p>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export default ExerciseSkeleton;
